import { Lead } from '@otw/models/leads/lead';

/**
 * Extracts unique truck options from leads' truck requests
 * This function processes all leads and extracts unique makes, models, sizes, types, and years
 * that are actually present in the leads' truck requests.
 * 
 * @param leads - Array of leads to extract truck options from
 * @returns Object containing arrays of unique truck options
 */
export const extractUniqueTruckOptions = (leads: Lead[]) => {
  const allMakes = new Set<string>();
  const allModels = new Set<string>();
  const allSizes = new Set<string>();
  const allTypes = new Set<string>();
  const allYears = new Set<string>();
  
  leads.forEach(lead => {
    if (lead.truckRequest?.types) {
      lead.truckRequest.types.forEach(truckType => {
        // Extract makes (handle both array and string)
        if (truckType.make) {
          if (Array.isArray(truckType.make)) {
            (truckType.make as string[]).forEach(make => {
              if (make && typeof make === 'string' && make.trim() !== '') {
                allMakes.add(make);
              }
            });
          } else if (typeof truckType.make === 'string' && (truckType.make as string).trim() !== '') {
            allMakes.add(truckType.make as string);
          }
        }
        
        // Extract models (handle both array and string)
        if (truckType.model) {
          if (Array.isArray(truckType.model)) {
            (truckType.model as string[]).forEach(model => {
              if (model && typeof model === 'string' && model.trim() !== '') {
                allModels.add(model);
              }
            });
          } else if (typeof truckType.model === 'string' && (truckType.model as string).trim() !== '') {
            allModels.add(truckType.model as string);
          }
        }
        
        // Extract sizes (handle both array and string)
        if (truckType.size) {
          if (Array.isArray(truckType.size)) {
            (truckType.size as string[]).forEach(size => {
              if (size && typeof size === 'string' && size.trim() !== '') {
                allSizes.add(size);
              }
            });
          } else if (typeof truckType.size === 'string' && (truckType.size as string).trim() !== '') {
            allSizes.add(truckType.size as string);
          }
        }
        
        // Extract types (handle both array and string)
        if (truckType.type) {
          if (Array.isArray(truckType.type)) {
            (truckType.type as string[]).forEach(type => {
              if (type && typeof type === 'string' && type.trim() !== '') {
                allTypes.add(type);
              }
            });
          } else if (typeof truckType.type === 'string' && (truckType.type as string).trim() !== '') {
            allTypes.add(truckType.type as string);
          }
        }
        
        // Extract years (minYear)
        if (truckType.minYear && typeof truckType.minYear === 'number') {
          allYears.add(truckType.minYear.toString());
        }
      });
    }
  });
  
  return {
    makes: Array.from(allMakes).sort(),
    models: Array.from(allModels).sort(),
    sizes: Array.from(allSizes).sort(),
    types: Array.from(allTypes).sort(),
    years: Array.from(allYears).sort()
  };
};

/**
 * Interface for truck filter criteria
 */
export interface TruckFilterCriteria {
  type?: string;
  make?: string;
  model?: string;
  size?: string;
  year?: number;
}

/**
 * Filters leads based on truck criteria
 * This function checks if any of the lead's truck types match the specified criteria
 * 
 * @param leads - Array of leads to filter
 * @param criteria - Truck filter criteria
 * @returns Filtered array of leads
 */
export const filterLeadsByTruckCriteria = (leads: Lead[], criteria: TruckFilterCriteria): Lead[] => {
  return leads.filter(lead => {
    if (!lead.truckRequest?.types) return false;
    
    return lead.truckRequest.types.some(truckType => {
      // Check type
      if (criteria.type) {
        if (Array.isArray(truckType.type)) {
          if (!(truckType.type as string[]).includes(criteria.type)) return false;
        } else if (typeof truckType.type === 'string') {
          if ((truckType.type as string) !== criteria.type) return false;
        } else {
          return false;
        }
      }
      
      // Check make
      if (criteria.make) {
        if (Array.isArray(truckType.make)) {
          if (!(truckType.make as string[]).includes(criteria.make)) return false;
        } else if (typeof truckType.make === 'string') {
          if ((truckType.make as string) !== criteria.make) return false;
        } else {
          return false;
        }
      }
      
      // Check model
      if (criteria.model) {
        if (Array.isArray(truckType.model)) {
          if (!(truckType.model as string[]).includes(criteria.model)) return false;
        } else if (typeof truckType.model === 'string') {
          if ((truckType.model as string) !== criteria.model) return false;
        } else {
          return false;
        }
      }
      
      // Check size
      if (criteria.size) {
        if (Array.isArray(truckType.size)) {
          if (!(truckType.size as string[]).includes(criteria.size)) return false;
        } else if (typeof truckType.size === 'string') {
          if ((truckType.size as string) !== criteria.size) return false;
        } else {
          return false;
        }
      }
      
      // Check year
      if (criteria.year && (!truckType.minYear || truckType.minYear !== criteria.year)) {
        return false;
      }
      
      return true;
    });
  });
}; 